import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { overlayClose } from "../../../../shared/utils/overlayClose";
import { ClasificacionInput } from "./ClasificacionInput";

/**
 * Actividades de flujo de efectivo sugeridas. Espejo de ACTIVIDADES_FE en
 * backend/apps/contabilidad/api/clasificacion_reportes.py — son sugerencias del
 * combo, no una restricción: el campo admite texto libre para que cada cliente
 * use el vocabulario de su contador.
 */
const ACTIVIDADES_FE = [
  "Efectivo",
  "Operación - resultado",
  "Operación - capital de trabajo",
  "Operación - depreciación",
  "Operación - depreciación acumulada",
  "Inversión - PPE",
  "Inversión - otros activos",
  "Inversión - intereses cobrados",
  "Financiación - deuda",
  "Financiación - patrimonio",
  "Financiación - intereses pagados",
  "Financiación - parte relacionada",
  "No aplica",
];

const OVERLAY = {
  position: "fixed", inset: 0, zIndex: 1000,
  background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 14, boxSizing: "border-box",
};

/** Una tarjeta de checkbox. Las cuatro banderas se veían idénticas salvo el
 *  color, repetidas a mano; ahora comparten forma y se acomodan en grid. */
function FlagCard({ color, checked, disabled, onChange, titulo, descripcion, badge }) {
  return (
    <div style={{
      background: `rgba(${color},0.06)`,
      border: `1px solid rgba(${color},0.2)`,
      borderRadius: 8, padding: "12px 14px",
    }}>
      <label style={{
        display: "flex", alignItems: "flex-start", gap: 10,
        cursor: disabled ? "default" : "pointer",
      }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          style={{ accentColor: `rgb(${color})`, marginTop: 2, flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "var(--text-primary)", fontSize: "0.85rem", fontWeight: 600 }}>
            {titulo}
            {badge && (
              <span style={{
                marginLeft: 8, fontSize: "0.7rem", color: `rgb(${color})`,
                background: `rgba(${color},0.15)`, padding: "1px 6px", borderRadius: 4,
              }}>
                {badge}
              </span>
            )}
          </div>
          <div style={{ color: "var(--text-muted)", fontSize: "0.75rem", marginTop: 2 }}>
            {descripcion}
          </div>
        </div>
      </label>
    </div>
  );
}

/**
 * CuentaModal — modal para crear o editar una cuenta contable.
 *
 * Props:
 *   mode    → "create" | "edit"
 *   padre   → objeto CuentaContable (padre bajo el que se crea) — solo en create
 *   cuenta  → objeto CuentaContable a editar — solo en edit
 *   onClose → cerrar el modal
 *   onSave  → async fn(data) → llamar al hook
 *   clasificacionesExistentes → string[] — sugerencias para el combo de
 *             clasificación (las ya usadas en el catálogo del condominio)
 */
export function CuentaModal({ mode, padre, cuenta, onClose, onSave, clasificacionesExistentes = [] }) {
  const { t } = useTranslation();
  const isEdit = mode === "edit";

  // El modal llegó a tener seis bloques apilados: en pantallas bajas el
  // contenido pasaba del alto de la ventana y, al estar centrado en el overlay,
  // se recortaba arriba Y abajo sin scroll posible — los botones quedaban
  // inalcanzables. Ahora header y acciones son fijos y el cuerpo scrollea.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 720px)");
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const [form,   setForm]   = useState({
    nombre:      isEdit ? cuenta?.nombre      : "",
    nombre_en:   isEdit ? (cuenta?.nombre_en ?? "") : "",
    descripcion: isEdit ? cuenta?.descripcion : "",
    is_detalle:  isEdit ? cuenta?.is_detalle  : false,
    es_costo_venta: isEdit ? (cuenta?.es_costo_venta ?? false) : false,
    es_recuperable: isEdit ? (cuenta?.es_recuperable ?? false) : false,
    es_resultado_periodo: isEdit ? (cuenta?.es_resultado_periodo ?? false) : false,
    clasificacion:  isEdit ? (cuenta?.clasificacion ?? "") : "",
    categoria_niif18: isEdit ? (cuenta?.categoria_niif18 ?? "") : "",
    actividad_fe:   isEdit ? (cuenta?.actividad_fe ?? "") : "",
    signo_presentacion: isEdit ? (cuenta?.signo_presentacion ?? 1) : 1,
    padre:       padre?.id ?? null,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [apiErr, setApiErr] = useState("");

  const nivelNuevo   = padre ? padre.nivel + 1 : null;
  const esNivel4     = nivelNuevo === 4;
  const puedeDetalle = isEdit
    ? !cuenta?.subcuentas?.length && cuenta?.nivel > 1
    : true;
  const tipoCuenta   = isEdit ? cuenta?.tipo : padre?.tipo;
  const esGasto      = tipoCuenta === "GASTO";
  const esIngresoOGasto = tipoCuenta === "GASTO" || tipoCuenta === "INGRESO";
  const esPatrimonio = tipoCuenta === "PATRIMONIO";

  useEffect(() => {
    if (esNivel4) setForm((p) => ({ ...p, is_detalle: true }));
  }, [esNivel4]);

  function validate() {
    const e = {};
    if (!form.nombre.trim()) e.nombre = t("catalogo.nombreObligatorio");
    if (form.nombre.length > 150) e.nombre = t("catalogo.nombreMaximo");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setApiErr("");
    try {
      await onSave(isEdit ? { ...form } : { ...form, padre: padre.id });
      onClose();
    } catch (e) {
      setApiErr(e.message || "Error desconocido");
    } finally {
      setSaving(false);
    }
  }

  const tituloAccion = isEdit ? t("catalogo.editarCuenta") : t("catalogo.nuevaSubcuenta");
  const subtitulo = isEdit
    ? `${cuenta?.codigo} — ${cuenta?.nombre}`
    : `${t("catalogo.bajo")}: ${padre?.codigo} — ${padre?.nombre}`;

  const inpStyle = (err) => ({
    width: "100%", padding: "9px 12px",
    background: "var(--bg-input)",
    border: `1px solid ${err ? "#ef4444" : "var(--border-input)"}`,
    borderRadius: 7, color: "var(--text-primary)", fontSize: "0.87rem",
    outline: "none", boxSizing: "border-box", marginTop: 4,
  });

  const labelStyle = {
    display: "block", color: "var(--text-secondary)", fontSize: "0.72rem",
    fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px",
  };

  const opcional = (
    <span style={{
      color: "var(--text-muted)", fontSize: "0.68rem",
      fontWeight: 400, textTransform: "none",
    }}>
      ({t("common.opcional")})
    </span>
  );

  const pad     = isMobile ? 18 : 26;
  const dosCols = { display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 };
  // auto-fit deja una sola columna cuando solo hay una bandera visible, y dos
  // cuando el tipo de cuenta habilita varias.
  const gridFlags = {
    display: "grid", gap: 12,
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(300px, 1fr))",
  };

  return (
    <div style={OVERLAY} {...overlayClose(onClose)}>
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 12, width: "min(780px, 100%)",
        maxHeight: "calc(100dvh - 28px)",
        display: "flex", flexDirection: "column",
        boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
      }} onClick={(e) => e.stopPropagation()}>

        {/* Header — fijo */}
        <div style={{
          flexShrink: 0, padding: `${pad}px ${pad}px 16px`,
          borderBottom: "1px solid var(--border-card)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <div style={{
            background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.3)",
            borderRadius: 8, padding: "7px 9px", color: "#f97316", flexShrink: 0,
          }}>
            <i className={`fa-solid ${isEdit ? "fa-pen" : "fa-plus"}`} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
              {tituloAccion}
            </h3>
            <p style={{
              margin: 0, color: "var(--text-muted)", fontSize: "0.75rem",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {subtitulo}
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSave}
          style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          {/* Cuerpo — scrolleable */}
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: `18px ${pad}px` }}>

            {/* Nombre + Nombre en inglés */}
            <div style={{ ...dosCols, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>
                  {t("catalogo.nombre")} <span style={{ color: "#f87171" }}>*</span>
                </label>
                <input
                  style={inpStyle(errors.nombre)}
                  value={form.nombre}
                  onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
                  placeholder="Ej: Efectivo en caja"
                  autoFocus
                />
                {errors.nombre && (
                  <div style={{ color: "#f87171", fontSize: "0.75rem", marginTop: 3 }}>
                    {errors.nombre}
                  </div>
                )}
              </div>
              <div>
                <label style={labelStyle}>
                  {t("catalogo.nombreEn")} {opcional}
                </label>
                <input
                  style={inpStyle(false)}
                  value={form.nombre_en}
                  onChange={(e) => setForm((p) => ({ ...p, nombre_en: e.target.value }))}
                  placeholder={t("catalogo.nombreEnPlaceholder")}
                />
                <div style={{ color: "var(--text-muted)", fontSize: "0.72rem", marginTop: 3 }}>
                  {t("catalogo.nombreEnDesc")}
                </div>
              </div>
            </div>

            {/* Descripción */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>
                {t("common.descripcion")} {opcional}
              </label>
              <textarea
                style={{ ...inpStyle(false), resize: "vertical", minHeight: 60 }}
                value={form.descripcion}
                onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))}
                placeholder="Descripción o notas contables..."
              />
            </div>

            {/* Banderas — se acomodan en columnas según cuántas aplican */}
            <div style={{ ...gridFlags, marginBottom: 16 }}>
              {puedeDetalle && (
                <FlagCard
                  color="249,115,22"
                  checked={form.is_detalle || esNivel4}
                  disabled={esNivel4}
                  onChange={(e) => setForm((p) => ({ ...p, is_detalle: e.target.checked }))}
                  titulo={t("catalogo.cuentaDetalle")}
                  descripcion={t("catalogo.cuentaDetalleDesc")}
                  badge={esNivel4 ? t("catalogo.nivel4Obligatorio") : null}
                />
              )}
              {esGasto && (
                <FlagCard
                  color="249,115,22"
                  checked={form.es_costo_venta}
                  onChange={(e) => setForm((p) => ({ ...p, es_costo_venta: e.target.checked }))}
                  titulo={t("catalogo.esCostoVenta")}
                  descripcion={t("catalogo.esCostoVentaDesc")}
                />
              )}
              {esIngresoOGasto && (
                <FlagCard
                  color="59,130,246"
                  checked={form.es_recuperable}
                  onChange={(e) => setForm((p) => ({ ...p, es_recuperable: e.target.checked }))}
                  titulo={t("catalogo.esRecuperable")}
                  descripcion={t("catalogo.esRecuperableDesc")}
                />
              )}
              {esPatrimonio && (
                <FlagCard
                  color="168,85,247"
                  checked={form.es_resultado_periodo}
                  onChange={(e) => setForm((p) => ({ ...p, es_resultado_periodo: e.target.checked }))}
                  titulo={t("catalogo.esResultadoPeriodo")}
                  descripcion={t("catalogo.esResultadoPeriodoDesc")}
                />
              )}
            </div>

            {/* Categoría NIIF 18 + Clasificación. Si la cuenta no es de
                ingreso/gasto solo va Clasificación: a una sola columna, para
                que no quede media fila vacía al lado. */}
            <div style={esIngresoOGasto ? dosCols : { ...dosCols, gridTemplateColumns: "1fr" }}>
              {esIngresoOGasto && (
                <div>
                  <label style={labelStyle}>
                    {t("catalogo.categoriaNiif18")} {opcional}
                  </label>
                  <select
                    value={form.categoria_niif18}
                    onChange={(e) => setForm((p) => ({ ...p, categoria_niif18: e.target.value }))}
                    style={{ ...inpStyle(false), cursor: "pointer" }}
                  >
                    <option value="">{t("catalogo.categoriaNiif18Heredar")}</option>
                    <option value="OPERACION">{t("catalogo.niif18Operacion")}</option>
                    <option value="INVERSION">{t("catalogo.niif18Inversion")}</option>
                    <option value="FINANCIAMIENTO">{t("catalogo.niif18Financiamiento")}</option>
                    <option value="IMPUESTO">{t("catalogo.niif18Impuesto")}</option>
                  </select>
                  <div style={{ color: "var(--text-muted)", fontSize: "0.72rem", marginTop: 3 }}>
                    {t("catalogo.categoriaNiif18Desc")}
                  </div>
                </div>
              )}
              <div>
                <label style={labelStyle}>
                  {t("catalogo.clasificacion")} {opcional}
                </label>
                <div style={{ marginTop: 4 }}>
                  <ClasificacionInput
                    value={form.clasificacion}
                    onChange={(v) => setForm((p) => ({ ...p, clasificacion: v }))}
                    options={clasificacionesExistentes}
                    placeholder={t("catalogo.clasificacionPlaceholder")}
                  />
                </div>
                <div style={{ color: "var(--text-muted)", fontSize: "0.72rem", marginTop: 3 }}>
                  {t("catalogo.clasificacionDesc")}
                </div>
              </div>
            </div>

            {/* Actividad de flujo de efectivo + Signo de presentación. Son los
                dos ejes que faltaban para que los estados salgan armados sin
                retoques a mano: la actividad no se deduce de la categoría NIIF
                18 (Financiamiento se abre en deuda, patrimonio e intereses), y
                el signo es lo que permite restar depreciación acumulada o
                devoluciones dentro de su propio rubro. */}
            <div style={dosCols}>
              <div>
                <label style={labelStyle}>
                  {t("catalogo.actividadFe")} {opcional}
                </label>
                <div style={{ marginTop: 4 }}>
                  <ClasificacionInput
                    value={form.actividad_fe}
                    onChange={(v) => setForm((p) => ({ ...p, actividad_fe: v }))}
                    options={ACTIVIDADES_FE}
                    placeholder={t("catalogo.actividadFePlaceholder")}
                  />
                </div>
                <div style={{ color: "var(--text-muted)", fontSize: "0.72rem", marginTop: 3 }}>
                  {t("catalogo.actividadFeDesc")}
                </div>
              </div>
              <div>
                <label style={labelStyle}>
                  {t("catalogo.signoPresentacion")}
                </label>
                <select
                  value={form.signo_presentacion}
                  onChange={(e) => setForm((p) => ({
                    ...p, signo_presentacion: Number(e.target.value),
                  }))}
                  style={{ ...inpStyle(false), cursor: "pointer" }}
                >
                  <option value={1}>{t("catalogo.signoNormal")}</option>
                  <option value={-1}>{t("catalogo.signoInvertido")}</option>
                </select>
                <div style={{ color: "var(--text-muted)", fontSize: "0.72rem", marginTop: 3 }}>
                  {t("catalogo.signoPresentacionDesc")}
                </div>
              </div>
            </div>

            {/* Error de API */}
            {apiErr && (
              <div style={{
                background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 6, padding: "8px 12px", marginTop: 16,
                color: "#f87171", fontSize: "0.8rem",
                display: "flex", alignItems: "flex-start", gap: 6,
              }}>
                <i className="fa-solid fa-circle-exclamation" style={{ marginTop: 3 }} />
                <span>{apiErr}</span>
              </div>
            )}
          </div>

          {/* Acciones — fijas */}
          <div style={{
            flexShrink: 0, padding: `14px ${pad}px`,
            borderTop: "1px solid var(--border-card)",
            display: "flex", gap: 8,
            justifyContent: isMobile ? "stretch" : "flex-end",
            flexDirection: isMobile ? "column-reverse" : "row",
          }}>
            <button type="button" onClick={onClose}
              style={{
                padding: "10px 18px", background: "transparent",
                border: "1px solid var(--border-card)", borderRadius: 7,
                color: "var(--text-secondary)", cursor: "pointer", fontSize: "0.84rem",
              }}>
              {t("common.cancelar")}
            </button>
            <button type="submit" disabled={saving}
              style={{
                padding: "10px 20px", background: "#f97316",
                border: "none", borderRadius: 7,
                color: "#fff", fontWeight: 600,
                cursor: saving ? "not-allowed" : "pointer",
                fontSize: "0.84rem", opacity: saving ? 0.7 : 1,
              }}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 6 }} />{t("catalogo.guardando")}</>
                : isEdit ? t("catalogo.guardarCambios") : t("catalogo.crearCuenta")
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
